import { gleamerT, GleamerKey } from '@gleamer/ui';
import type { ErrorObject } from 'ajv';

type Schema = Record<string, unknown>;

const TYPE_EXPECTED: Record<string, GleamerKey> = {
  string: 'validation.expects.string',
  number: 'validation.expects.number',
  integer: 'validation.expects.integer',
  boolean: 'validation.expects.boolean',
  array: 'validation.expects.array',
  object: 'validation.expects.object',
};

type TFunction = Parameters<typeof gleamerT>[0];

/**
 * One of a field's choices: its title or value as the schema gives it (shown unchanged), or, for a boolean without
 * a title, our own yes / no (translated).
 */
type Choice = { text: string } | { key: GleamerKey };

/** What a field expects, as a Gleamer string and its parts: translated where it is shown. */
type Readable = { key: GleamerKey; field: string; choices?: Choice[] };

export type ReadableError = ErrorObject & { readable?: Readable };

function asSchemas(value: unknown): Schema[] {
  if (Array.isArray(value)) {
    return value.filter(v => v && typeof v === 'object');
  }
  return value && typeof value === 'object' ? [value as Schema] : [];
}

/** The schemas a property may come from: the schema itself and its conditional and combined parts. */
function branches(schema: Schema): Schema[] {
  const found = [schema];
  for (const part of ['allOf', 'anyOf', 'oneOf', 'then', 'else'] as const) {
    asSchemas(schema[part]).forEach(sub => found.push(...branches(sub)));
  }
  Object.values((schema.dependencies as Record<string, unknown>) ?? {}).forEach(
    dependency =>
      asSchemas(dependency).forEach(sub => found.push(...branches(sub)))
  );
  return found;
}

type Field = { name: string; schema: Schema };

/** The schema of the field an error points at (`/0/primarycancer`), looked up through items and branches. */
function fieldOf(root: Schema, instancePath: string): Field | null {
  const segments = instancePath
    .split('/')
    .slice(1)
    .map(segment => segment.replace(/~1/g, '/').replace(/~0/g, '~'));
  let candidates = [root];
  let name = '';
  for (const segment of segments) {
    const next: Schema[] = [];
    candidates.forEach(candidate =>
      branches(candidate).forEach(branch => {
        if (/^\d+$/.test(segment) && branch.items) {
          next.push(...asSchemas(branch.items));
        }
        const property = (branch.properties as Record<string, unknown>)?.[
          segment
        ];
        next.push(...asSchemas(property));
      })
    );
    if (next.length === 0) {
      return null;
    }
    candidates = next;
    name = segment;
  }
  // an error on an item of the array (`/0`) is about the whole form, not a field
  if (!name || /^\d+$/.test(name)) {
    return null;
  }
  // a field is often declared once with its title and options, and narrowed elsewhere (`const` in a branch)
  const described =
    candidates.find(
      candidate =>
        candidate.title || candidate.oneOf || candidate.anyOf || candidate.enum
    ) ?? candidates[0];
  return { name, schema: described };
}

function choice(value: unknown, title?: unknown): Choice {
  if (title !== undefined) {
    return { text: String(title) };
  }
  if (typeof value === 'boolean') {
    return { key: value ? 'validation.true' : 'validation.false' };
  }
  return { text: String(value) };
}

function choices(schema: Schema): Choice[] | null {
  const options = asSchemas(schema.oneOf ?? schema.anyOf);
  if (options.length > 0 && options.every(option => 'const' in option)) {
    return options.map(option => choice(option.const, option.title));
  }
  if (Array.isArray(schema.enum)) {
    return schema.enum.map(value => choice(value));
  }
  return null;
}

/** A readable message in the user's language: our words translated, the schema's titles and values as they are. */
function show(
  t: TFunction | undefined,
  { key, field, choices: options }: Readable
) {
  return gleamerT(t, key, {
    field,
    options: (options ?? [])
      .map(option => ('key' in option ? gleamerT(t, option.key) : option.text))
      .join(', '),
  });
}

/**
 * The ajv keywords that say a field is not one of its choices or not of its type: those are rewritten. Any other
 * (`maximum`, `minLength`, `pattern`, `minItems`…) keeps ajv's message, which says the limit.
 */
const REWRITTEN = ['type', 'const', 'enum', 'oneOf', 'anyOf'];

function readable(error: ErrorObject, field: Field): Readable | null {
  if (!REWRITTEN.includes(error.keyword)) {
    return null;
  }
  const label = String(field.schema.title ?? field.name);
  const options = choices(field.schema);
  if (options) {
    return { key: 'validation.chooseOneOf', field: label, choices: options };
  }
  const type = field.schema.type;
  if (typeof type === 'string' && TYPE_EXPECTED[type]) {
    return { key: TYPE_EXPECTED[type], field: label };
  }
  return null;
}

/**
 * What a form still expects, in its own words: the field's title and its choices (`Cancer primitif renseignés:
 * choose one of Oui, Non`) instead of ajv's `must be equal to constant`, `must match exactly one schema in oneOf`
 * or `must be string`; for a limit (`maximum`, `minLength`…), the field's title then ajv's message. Messages
 * written in the schema (`errorMessage`), and errors that point at no field, are kept as they are. The message is
 * in English; `readable` says what to translate where it is shown.
 */
export function withReadableMessages(
  errors: ErrorObject[] | null | undefined,
  rootSchema: Schema
): ReadableError[] | null | undefined {
  return (
    errors?.map(error => {
      const field =
        error.keyword === 'errorMessage'
          ? null
          : fieldOf(rootSchema, error.instancePath ?? '');
      if (!field) {
        return error;
      }
      const found = readable(error, field);
      return found
        ? { ...error, message: show(undefined, found), readable: found }
        : {
            ...error,
            message: `${String(field.schema.title ?? field.name)}: ${error.message}`,
          };
    }) ?? errors
  );
}

/**
 * The lines the "Pass cannot be saved" popover shows for a list of errors, in the user's language: each message
 * once, without ajv's `must match "then" schema`, which only sums up the errors of the condition's fields listed
 * next to it.
 */
export function messagesToShow(
  errors: unknown[] = [],
  t?: TFunction
): string[] {
  const flat = errors
    .flat(Infinity)
    .filter(Boolean) as Partial<ReadableError>[];
  return [
    ...new Set(
      flat
        .filter(error => error.keyword !== 'if')
        .map(error =>
          error.readable
            ? show(t, error.readable)
            : error.message
              ? error.message.replace(/"/g, '')
              : 'Error'
        )
    ),
  ];
}
